import { useEffect, useMemo, useRef } from 'react';
import { useParams, NavLink, Link } from 'react-router-dom';

import { renderMarkdown } from '../lib/markdown.js';

// All wiki pages are bundled at build time via Vite's `?raw` import so the
// reader doesn't need a separate fetch round-trip and the wiki stays in
// sync with the SPA's deploy.
import readmeMd      from '../../docs/wiki/README.md?raw';
import gettingMd     from '../../docs/wiki/getting-started.md?raw';
import crewMd        from '../../docs/wiki/crew.md?raw';
import foremanMd     from '../../docs/wiki/foreman.md?raw';
import bookkeeperMd  from '../../docs/wiki/bookkeeper.md?raw';
import adminMd       from '../../docs/wiki/admin.md?raw';
import payRunsMd     from '../../docs/wiki/pay-runs.md?raw';
import discordMd     from '../../docs/wiki/discord.md?raw';
import opsMd         from '../../docs/wiki/ops.md?raw';
import apiMd         from '../../docs/wiki/api.md?raw';

const PAGES = [
  { slug: 'README',           label: 'Overview',        icon: 'ph-book-open',    md: readmeMd },
  { slug: 'getting-started',  label: 'Getting started', icon: 'ph-rocket-launch', md: gettingMd },
  { slug: 'crew',             label: 'Crew',            icon: 'ph-user',         md: crewMd },
  { slug: 'foreman',          label: 'Foreman',         icon: 'ph-users-three',  md: foremanMd },
  { slug: 'bookkeeper',       label: 'Bookkeeper',      icon: 'ph-calculator',   md: bookkeeperMd },
  { slug: 'admin',            label: 'Admin',           icon: 'ph-shield',       md: adminMd },
  { slug: 'pay-runs',         label: 'Pay runs',        icon: 'ph-money',        md: payRunsMd },
  { slug: 'discord',          label: 'Discord',         icon: 'ph-discord-logo', md: discordMd },
  { slug: 'ops',              label: 'Operations',      icon: 'ph-gear',         md: opsMd },
  { slug: 'api',              label: 'API reference',   icon: 'ph-code',         md: apiMd },
];

const BY_SLUG = Object.fromEntries(PAGES.map(p => [p.slug, p]));

// /wiki              → renders the index
// /wiki/<slug>       → renders the matching page; falls back to a "not found"
// /wiki/<slug>#anchor → renders + scrolls to the heading
export default function WikiPage() {
  const { slug = 'README' } = useParams();
  const page = BY_SLUG[slug] || null;
  const html = useMemo(() => (page ? renderMarkdown(page.md) : ''), [page]);
  const contentRef = useRef(null);

  // Hash-scroll after each page render. The renderer slugifies heading text
  // into id="..." attributes so #my-section anchors resolve.
  useEffect(() => {
    if (!page) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash) {
      contentRef.current?.scrollTo?.({ top: 0, behavior: 'instant' });
      return;
    }
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page, slug]);

  return (
    <div className="wiki-shell">
      <aside className="wiki-sidebar">
        <h3 className="wiki-sidebar-title">
          <i className="ph ph-book-open" /> Wiki
        </h3>
        <nav>
          <ul>
            {PAGES.map(p => (
              <li key={p.slug}>
                <NavLink
                  to={`/wiki/${p.slug}`}
                  className={({ isActive }) => 'wiki-link' + (isActive || (slug === p.slug) ? ' active' : '')}
                  end
                >
                  <i className={'ph ' + p.icon} /> {p.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="wiki-sidebar-foot">
          docs/wiki/{slug}.md
        </div>
      </aside>
      <article className="wiki-content" ref={contentRef}>
        {page ? (
          <div
            className="wiki-rendered"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        ) : (
          <div className="wiki-rendered">
            <h1>Page not found</h1>
            <p>No wiki page at <code>{slug}</code>. <Link to="/wiki">Back to overview</Link>.</p>
          </div>
        )}
      </article>
    </div>
  );
}
